import { Fragment, type KeyboardEvent, type ReactElement, useRef, useState } from "react"
import { useEffect } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useSelector } from "react-redux"

import CallMadeIcon from "@mui/icons-material/CallMade"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined"
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined"
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined"
import { Grid } from "@mui/material"
import { Button, TextArea, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import { type StandardErrorType, withErrorHandler } from "../../utils/ErrorHandling"
import { KonanAssistantApi } from "./Api"
import { KonanAssistantDrawer } from "./components/KonanAssistantDrawer"
import { Message } from "./components/Message"
import { Suggestions } from "./components/Suggestions"
import { SummaryMessage } from "./components/SummaryMessage"
import { useCreateComponentsHandler } from "./helpers/AutoRun"
import type { AssistantAIContent, AssistantMessage, IMessage } from "./interfaces"

import styles from "./KonanAssistant.module.scss"

export function KonanAssistant(): ReactElement {
  const { t } = useTranslation("KonanAssistant")
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const [sessionId, setSessionId] = useState<string | undefined>(undefined)
  const [messages, setMessages] = useState<IMessage[]>([])
  const [currentMessage, setCurrentMessage] = useState<string>("")
  const [isCreating, setIsCreating] = useState(false)

  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const lastMessageRef = useRef<{ sessionId: string; question: string } | null>(null)

  const { createComponents } = useCreateComponentsHandler({ setMessages, setIsCreating })

  const createLLMSessionMutation = useMutation<
    AxiosResponse,
    AxiosError<StandardErrorType>,
    { projectId: string; agent_type: "COMPONENT_CREATION_AGENT" | "CHAT_DATA_AGENT" }
  >(KonanAssistantApi.createSession, {
    onSuccess: () => {
      queryClient.invalidateQueries(["konan-sessions", currentProject?.uuid])
    },
  })

  const sendMessageMutation = useMutation<
    AxiosResponse,
    AxiosError<StandardErrorType>,
    { sessionId: string; question: string }
  >(KonanAssistantApi.addMessageToSession, {
    onSuccess: ({ data }) => {
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          content: buildSummaryMessage(data, prev.length, false, false),
          cancelled: false,
          completed: false,
        },
      ])
    },
  })

  const retrieveMessagesMutation = useMutation<
    AxiosResponse<AssistantMessage[]>,
    AxiosError<StandardErrorType>,
    { sessionId: string }
  >(KonanAssistantApi.retrieveSessionMessages, {
    onSuccess: ({ data }) => {
      const formattedMessages: IMessage[] = data.map((msg, index) => {
        if (msg.type === "human") {
          return {
            sender: "user",
            content: msg.content,
          }
        }
        return {
          sender: "bot",
          content: buildSummaryMessage({ responses: msg.content.responses }, index, false, false),
          cancelled: false,
        }
      })

      setMessages(formattedMessages)
    },
    onError: withErrorHandler(() => {
      setMessages([{ sender: "bot", content: t("messages.failedToLoadHistory") }])
    }),
  })

  const buildSummaryMessage = (
    data: AssistantAIContent,
    messageIndex: number,
    isCancelled: boolean,
    isCompleted: boolean,
  ): ReactElement => {
    return (
      <SummaryMessage
        bulletPoint={data.responses.map(({ comment, component_name }) => ({
          component: component_name,
          point: comment,
        }))}
        actions={
          isCompleted || isCancelled
            ? [
                <Grid item display="flex" alignItems="center" gap={0.5} key="confirmation-message">
                  {isCancelled ? (
                    <StopCircleOutlinedIcon style={{ fontSize: "1.1rem", color: `var(--red-text-2)` }} />
                  ) : (
                    <PlayArrowOutlinedIcon style={{ fontSize: "1.1rem", color: `var(--blue-text-2)` }} />
                  )}
                  <Typography variant="label-bold" style={{ color: `var(--${isCancelled ? "red" : "blue"}-text-2)` }}>
                    {isCancelled ? t("status.runCancelled") : t("status.runExecuted")}
                  </Typography>
                </Grid>,
              ]
            : [
                <Button
                  key="accept"
                  startIcon={<PlayArrowOutlinedIcon fontSize="small" />}
                  onClick={() => {
                    setIsCreating(true)
                    createComponents(data.responses)
                    setMessages((prev) =>
                      prev.map((msg, i) => {
                        if (i === messageIndex) {
                          return {
                            ...msg,
                            completed: true,
                            content: buildSummaryMessage(data, i, isCancelled, true),
                          }
                        }
                        return msg
                      }),
                    )
                  }}
                >
                  {t("actions.acceptAndRun")}
                </Button>,
                <Button
                  key="cancel"
                  startIcon={<CloseOutlinedIcon fontSize="small" />}
                  onClick={() =>
                    setMessages((prev) =>
                      prev.map((msg, i) => {
                        if (i === messageIndex) {
                          return {
                            ...msg,
                            cancelled: true,
                            content: buildSummaryMessage(data, i, true, isCompleted),
                          }
                        }
                        return msg
                      }),
                    )
                  }
                >
                  {t("actions.cancel")}
                </Button>,
              ]
        }
      />
    )
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isCreating])

  /**
   * Scrolls the messages container to the bottom smoothly.
   * This function uses a timeout to ensure the scroll happens after the new message is rendered.
   */
  const scrollToBottom = (): void => {
    const messagesContainer = messagesContainerRef.current
    if (messagesContainer) {
      messagesContainer.scrollTo({
        top: messagesContainer.scrollHeight,
        behavior: "smooth",
      })
    }
  }

  /**
   * Handles sending a user message.
   * If the user message is not empty, it adds the message to the messages state,
   * clears the user message input, and scrolls the messages container to the bottom.
   */
  const handleSendMessage = async (text?: string): Promise<void> => {
    const message = text ?? currentMessage

    if (message.trim() !== "") {
      if (!sessionId) {
        const session = await createLLMSessionMutation.mutateAsync({
          projectId: currentProject?.uuid as string,
          agent_type: "COMPONENT_CREATION_AGENT",
        })
        setSessionId(() => session.data.uuid)
        lastMessageRef.current = { sessionId: session.data.uuid, question: message }
        sendMessageMutation.mutateAsync({ sessionId: session.data.uuid as string, question: message })
      } else {
        lastMessageRef.current = { sessionId: sessionId, question: message }
        sendMessageMutation.mutateAsync({ sessionId: sessionId, question: message })
      }

      setCurrentMessage("")
      scrollToBottom()
      setMessages((prev) => [...prev, { sender: "user", content: message }])
    }
    // Focus the text area after stopping the stream
    textAreaRef?.current?.focus()
  }

  /**
   * Handles the key down event for the text area.
   * Sends the message when the Enter key is pressed without the Shift key.
   * @param e - The keyboard event.
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      // send user message
      handleSendMessage()
      // Prevent the default line break behavior
      e.preventDefault()
      // Ensure the message is not sent twice by stopping the event propagation
      e.stopPropagation()
    }
  }

  const handleNewChat = (): void => {
    setMessages([])
    setCurrentMessage("")
    setSessionId(undefined)
  }

  return (
    <div className={styles.root}>
      <KonanAssistantDrawer onNewChat={handleNewChat} />

      <div className={styles.chat}>
        {/* Empty state */}
        {messages.length === 0 ? (
          <Fragment>
            <div className={styles.emptyState}>
              <TipsAndUpdatesOutlinedIcon className={styles.emptyIcon} />
              <Typography variant="h3-bold" align="center">
                <Trans
                  i18nKey="emptyState.title"
                  components={{
                    konan: <span className={styles.konanText} />,
                  }}
                  ns="KonanAssistant"
                  values={{
                    konan: t("emptyState.konanHighlight"),
                  }}
                />
              </Typography>
              <Typography variant="p" align="center">
                {t("emptyState.subtitle")}
              </Typography>
            </div>

            <Suggestions onSelect={(suggestion) => handleSendMessage(suggestion)} />
          </Fragment>
        ) : (
          <div ref={messagesContainerRef} className={styles.messagesContainer}>
            {messages.map((message, index) => (
              <Message key={index} sender={message.sender} content={message.content} />
            ))}

            {/* TODO:: figure out a better way to condition the render of the Loading message */}
            {messages[messages.length - 1]?.sender === "user" &&
              (sendMessageMutation.isLoading || createLLMSessionMutation.isLoading) && (
                <Message sender="bot" isLoading content={t("status.generatingComponents")} />
              )}

            {/* Handles loading for the "Accept & run" action */}
            {isCreating && <Message sender="bot" isLoading content={t("status.generatingComponents")} />}

            {sendMessageMutation.isError && (
              <Message
                sender="bot"
                hasError
                content={t("messages.error")}
                onRetry={() => {
                  if (lastMessageRef.current) sendMessageMutation.mutate(lastMessageRef.current)
                }}
              />
            )}

            {retrieveMessagesMutation.isLoading && (
              <Message sender="bot" isLoading content={t("status.loadingChatHistory")} />
            )}
          </div>
        )}

        <div className={styles.footer}>
          {/* Message box */}
          <div className={styles.textAreaContainer}>
            <TextArea
              ref={textAreaRef}
              placeholder={t("messages.placeholder")}
              heightControl={{ lines: 3, isExpandable: true, maxRowsBeforeScroll: 5 }}
              hideDescription
              multiple
              fullWidth
              value={currentMessage}
              handleChange={(e) => setCurrentMessage((e.target as HTMLTextAreaElement).value)}
              onKeyDown={handleKeyDown}
            />

            <div
              className={`${styles.sendButtonContainer} ${currentMessage.trim().length > 0 ? styles.sendButtonContainerActive : ""}`}
            >
              <Button
                variant="primary-inverse"
                className={`${styles.sendButton}`}
                onClick={() => handleSendMessage()}
                disabled={
                  sendMessageMutation.isLoading ||
                  createLLMSessionMutation.isLoading ||
                  currentMessage.trim().length === 0
                }
              >
                <CallMadeIcon fontSize="small" />
              </Button>
            </div>
          </div>

          {/* Disclaimer */}
          <Typography variant="label-regular" align="center">
            {t("disclaimer")}
          </Typography>
        </div>
      </div>
    </div>
  )
}
